import type { NextStep } from '../data/reminderSchedule';
import type { Party } from '../audit/requirements';
import { formatDay } from './dates';

/**
 * B5 (owner rulings 2026-10-25): the "next step" sentences under an audit
 * clock and on a task page, from the steps nextAuditSteps / nextTaskSteps
 * work out. Pure.
 */

/** Who a clock's reminders go to (Flow 1: auditors' distro, the store account, else the DM). */
function clockWho(party: Party | null | undefined): string {
  return party === 'auditor' ? 'the Auditors' : party === 'store' ? 'the store' : 'the DM';
}

/**
 * Who Flow 1 escalates a clock to (QA 2026-10-25): an Auditor clock → the
 * GLOBAL escalation target; a store-team clock → the store's DM; a DM clock
 * → the district's escalation contact (else the global target).
 */
export interface EscalationNames {
  escalationName: string;
  globalEscalationName: string;
  dmName: string;
}

function escalatesTo(party: Party | null | undefined, names: EscalationNames): string {
  const name = party === 'auditor' ? names.globalEscalationName : party === 'store' ? names.dmName : names.escalationName;
  return name.trim();
}

export function auditStepsText(steps: readonly NextStep[], party: Party | null | undefined, names: EscalationNames): string {
  const who = clockWho(party);
  const to = escalatesTo(party, names);
  const parts = steps.flatMap((s) => {
    const day = formatDay(s.date);
    if (s.kind === 'due_now') return [`“Audit due” notice to ${who} ${day}`];
    // Nobody to escalate to (no contact / DM set) → Flow 1 sends nothing; say nothing.
    if (s.kind === 'escalation') return to ? [`escalated to ${to} ${day} if still not done`] : [];
    return [`reminder to ${who} ${day}`];
  });
  return capitalize(parts.join(' · '));
}

export function taskStepsText(steps: readonly NextStep[], dmName: string): string {
  if (steps.length === 0) return '';
  const parts = steps.map((s) => {
    const day = formatDay(s.date);
    if (s.kind === 'due_soon') return `due-soon reminder to the assignee ${day}`;
    // Flow 3 escalates only when the store has a DM.
    if (s.kind === 'escalation') return dmName.trim() ? `goes to the DM (${dmName.trim()}) ${day} if still overdue` : '';
    return `overdue reminder to the assignee ${day}`;
  }).filter(Boolean);
  return parts.length > 0 ? `Next: ${parts.join(' · ')}` : '';
}

function capitalize(s: string): string {
  return s ? s[0]!.toUpperCase() + s.slice(1) : s;
}
